import { Card } from "@affinity/components/Card";
import { bodyFont, colours } from "@affinity/constants/StyleConstants";
import { breakpointWidth, rem } from "@affinity/constants/StyleHelpers";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC, useCallback } from "react";
import styled from "styled-components";

interface OwnProps extends WidgetProps {
    fields: {
        link: LinkFieldValue;
        title: string;
        image: string;
        description: string;
        tag?: string;
        publishDate: Date;
    };
}

export const ArticleCard: FC<OwnProps> = ({ fields }) => {
    const link = useGetLink(fields.link);
    const history = useHistory();
    const onClick = useCallback(() => {
        if (link.url) {
            history.push(link.url);
        }
    }, [history, link.url]);

    return (
        <a href={link.url} onClick={onClick}>
            <Card variant="outline" color="lightBlue" header={<Image src={fields.image} alt="" />}>
                <Container>
                    {fields.tag && <Tag>{fields.tag}</Tag>}
                    <Title>{fields.title}</Title>
                    <p>{fields.description}</p>
                    <Time dateTime={new Date(fields.publishDate).toISOString()}>
                        <LocalizedDateTime
                            dateTime={new Date(fields.publishDate)}
                            options={{ year: "numeric", month: "short", day: "numeric" }}
                        />
                    </Time>
                </Container>
            </Card>
        </a>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(10)};
    line-height: 1.5em;
    font-size: ${rem(16)};
    padding: ${rem(10)} 0;
    @media screen and (min-width: ${breakpointWidth("md")}) {
        font-size: ${rem(18)};
    }
`;

const Title = styled.h2`
    font-weight: 700;
    margin: 0;
    padding: 0;
    line-height: 1.2em;
    font-size: ${rem(20)};
    font-family: ${bodyFont};
    color: ${colours.darkBlue};
    @media screen and (min-width: ${breakpointWidth("md")}) {
        font-size: ${rem(28)};
    }
    a:hover & {
        color: ${colours.green};
    }
`;

const Tag = styled.span`
    font-size: ${rem(15)};
    line-height: 1em;
    align-self: start;
    font-weight: 600;
    text-transform: uppercase;
    color: ${colours.darkBlue};
    background-color: ${colours.lightBlue};
    border-radius: ${rem(5)};
    padding: ${rem(8)} ${rem(5)};
`;

const Time = styled.time`
    font-size: ${rem(15)};
`;

const Image = styled.img`
    width: 100%;
    display: block;
    aspect-ratio: 16 / 9;
    object-fit: cover;
`;

const articleCard: WidgetModule = {
    component: ArticleCard,
    definition: {
        group: "Basic",
        icon: "newspaper",
        fieldDefinitions: [
            {
                name: "link",
                displayName: "Article link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: "image",
                displayName: "Article image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: "title",
                displayName: "Article title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: "description",
                displayName: "Article description",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: "tag",
                displayName: "Article tag",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "publishDate",
                displayName: "Article publish date",
                editorTemplate: "DateTimeField",
                defaultValue: new Date(),
                fieldType: "General",
                isRequired: true,
            },
        ],
    },
};

export default articleCard;
